import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CornerDownLeft } from 'lucide-react';

const findSlot = (key) => document.querySelector(`[data-testid="bracket-viewer"] [data-bk-slot="${CSS.escape(key)}"]`);

// Every writable spot on screen, column by column, top to bottom.
const orderedKeys = () => {
  const seen = new Set();
  return [...document.querySelectorAll('[data-testid="bracket-viewer"] [data-bk-slot]')]
    .map((el) => ({ key: el.getAttribute('data-bk-slot'), r: el.getBoundingClientRect() }))
    .sort((a, b) => (Math.abs(a.r.left - b.r.left) > 4 ? a.r.left - b.r.left : a.r.top - b.r.top))
    .map((s) => s.key)
    .filter((k) => (seen.has(k) ? false : seen.add(k)));
};

export const slotLabel = (key, teamLabels) => {
  if (key.startsWith('team:')) return teamLabels[key] || 'Team';
  if (key.startsWith('champ:')) return key.slice(6).replace(/\b\w+/g, (w) => w[0] + w.slice(1).toLowerCase());
  const [, label, side] = key.split(':');
  return `${label} · ${side === 'a' ? 'top' : 'bottom'} spot`;
};

// Inline text box laid over the clicked bracket spot; Enter/Tab moves to the next spot.
export const SlotEditor = ({ slotKey, value, label, onChange, onMove, onClose }) => {
  const [rect, setRect] = useState(null);
  const place = useCallback(() => {
    const el = findSlot(slotKey);
    if (el) setRect(el.getBoundingClientRect());
  }, [slotKey]);

  useLayoutEffect(place, [place, value]);
  useEffect(() => {
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [place]);

  const move = (dir) => {
    const keys = orderedKeys();
    const next = keys[(keys.indexOf(slotKey) + dir + keys.length) % keys.length];
    findSlot(next)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    onMove(next);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); move(e.shiftKey ? -1 : 1); }
  };

  if (!rect) return null;
  const width = Math.max(rect.width, 180);
  const height = Math.max(rect.height, 32);
  const left = Math.min(rect.left, window.innerWidth - width - 8);
  return createPortal(
    <div className="fixed z-[60] animate-in fade-in zoom-in-95 duration-150" style={{ left, top: rect.top + rect.height / 2 - height / 2, width }} data-testid="slot-editor">
      <div className="absolute -top-6 left-0 rounded-t-md bg-[#00247D] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white whitespace-nowrap"
        data-testid="slot-editor-label">{label}</div>
      <div className="flex items-center rounded-md rounded-tl-none border-2 border-[#00247D] bg-white shadow-xl">
        <input autoFocus value={value} maxLength={60} onChange={(e) => onChange(e.target.value)} onKeyDown={onKey} onBlur={onClose}
          placeholder="Type a name" style={{ height: height - 4 }} data-testid="slot-editor-input"
          className="min-w-0 flex-1 bg-transparent px-2 text-sm font-semibold text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400" />
        {value && (
          <button type="button" onMouseDown={(e) => { e.preventDefault(); onChange(''); }} className="px-1.5 text-slate-400 hover:text-[#CF142B]"
            aria-label="Clear name" data-testid="slot-editor-clear"><X className="w-4 h-4" /></button>
        )}
        <span className="hidden sm:flex items-center gap-1 pr-2 text-[10px] text-slate-400"><CornerDownLeft className="w-3 h-3" />next</span>
      </div>
    </div>,
    document.body,
  );
};
